import { useLanguage } from "@/contexts/LanguageContext";
import { CLASSIFICATION_CATEGORIES } from "@/lib/tourismClassification";
import { useMemo } from "react";

type Option = {
  value: string;
  label: string;
  en?: string;
  ja?: string;
  zh?: string;
  de?: string;
  fr?: string;
  code?: string;
};

const ALL_CATEGORIES: Option = {
  value: "all",
  label: "전체 카테고리",
  en: "All Categories",
  ja: "すべてのカテゴリ",
  zh: "全部分类",
  de: "Alle Kategorien",
  fr: "Toutes les categories",
  code: "",
};

export const useTourismOptions = () => {
  const { language } = useLanguage();

  const categories = useMemo<Option[]>(
    () => [
      ALL_CATEGORIES,
      ...CLASSIFICATION_CATEGORIES.map((category) => ({
        value: category.value,
        label: category.label,
        en: category.en,
        ja: category.ja,
        zh: category.zh,
        de: category.de,
        fr: category.fr,
        code: category.value,
      })),
    ],
    []
  );

  const regions = useMemo<Option[]>(
    () => [
      {
        value: "all",
        label: "전체 지역",
        en: "All Regions",
        ja: "すべての地域",
        zh: "全部地区",
        de: "Alle Regionen",
        fr: "Toutes les regions",
      },
      {
        value: "11",
        label: "서울특별시",
        en: "Seoul",
        ja: "ソウル",
        zh: "首尔",
        de: "Seoul",
        fr: "Seoul",
      },
      {
        value: "26",
        label: "부산광역시",
        en: "Busan",
        ja: "釜山",
        zh: "釜山",
        de: "Busan",
        fr: "Busan",
      },
      {
        value: "27",
        label: "대구광역시",
        en: "Daegu",
        ja: "大邱",
        zh: "大邱",
        de: "Daegu",
        fr: "Daegu",
      },
      {
        value: "28",
        label: "인천광역시",
        en: "Incheon",
        ja: "仁川",
        zh: "仁川",
        de: "Incheon",
        fr: "Incheon",
      },
      {
        value: "29",
        label: "광주광역시",
        en: "Gwangju",
        ja: "光州",
        zh: "光州",
        de: "Gwangju",
        fr: "Gwangju",
      },
      {
        value: "30",
        label: "대전광역시",
        en: "Daejeon",
        ja: "大田",
        zh: "大田",
        de: "Daejeon",
        fr: "Daejeon",
      },
      {
        value: "31",
        label: "울산광역시",
        en: "Ulsan",
        ja: "蔚山",
        zh: "蔚山",
        de: "Ulsan",
        fr: "Ulsan",
      },
      {
        value: "41",
        label: "경기도",
        en: "Gyeonggi-do",
        ja: "京畿道",
        zh: "京畿道",
        de: "Gyeonggi-do",
        fr: "Gyeonggi-do",
      },
      {
        value: "42",
        label: "강원특별자치도",
        en: "Gangwon-do",
        ja: "江原道",
        zh: "江原道",
        de: "Gangwon-do",
        fr: "Gangwon-do",
      },
      {
        value: "43",
        label: "충청북도",
        en: "North Chungcheong",
        ja: "忠清北道",
        zh: "忠清北道",
        de: "Nord-Chungcheong",
        fr: "Chungcheong du Nord",
      },
      {
        value: "44",
        label: "충청남도",
        en: "South Chungcheong",
        ja: "忠清南道",
        zh: "忠清南道",
        de: "Sued-Chungcheong",
        fr: "Chungcheong du Sud",
      },
      {
        value: "45",
        label: "전북특별자치도",
        en: "North Jeolla",
        ja: "全北特別自治道",
        zh: "全北特别自治道",
        de: "Nord-Jeolla",
        fr: "Jeolla du Nord",
      },
      {
        value: "46",
        label: "전라남도",
        en: "South Jeolla",
        ja: "全羅南道",
        zh: "全罗南道",
        de: "Sued-Jeolla",
        fr: "Jeolla du Sud",
      },
      {
        value: "47",
        label: "경상북도",
        en: "North Gyeongsang",
        ja: "慶尚北道",
        zh: "庆尚北道",
        de: "Nord-Gyeongsang",
        fr: "Gyeongsang du Nord",
      },
      {
        value: "48",
        label: "경상남도",
        en: "South Gyeongsang",
        ja: "慶尚南道",
        zh: "庆尚南道",
        de: "Sued-Gyeongsang",
        fr: "Gyeongsang du Sud",
      },
      {
        value: "50",
        label: "제주특별자치도",
        en: "Jeju Island",
        ja: "済州特別自治道",
        zh: "济州特别自治道",
        de: "Jeju-Insel",
        fr: "Ile de Jeju",
      },
    ],
    []
  );

  const getLocalizedLabel = (option: Option) => {
    if (language === "ko") return option.label;
    return option[language as keyof Omit<Option, "value" | "label" | "code">] || option.label;
  };

  return {
    categories: categories.map((cat) => ({
      ...cat,
      displayLabel: getLocalizedLabel(cat),
    })),
    regions: regions.map((reg) => ({
      ...reg,
      displayLabel: getLocalizedLabel(reg),
    })),
  };
};
